import rcss from '../../../Styles/reports.module.css';
import { observer } from "mobx-react-lite";
import { enhancedContentSourceLocation } from "../../../lib/ContentSource";
import { SearchResultDetailsView } from "./common/SearchResultDetailsView";
import { SearchResultsObserver } from "../SearchResultsObserver";
import { CausationGraphObserver } from '../../../lib/Analysis/CausationGraphObserver';
import { InferredTypeNamesBlockView } from './common/InferredTypeNamesBlockView';
import { IpReportData } from './IpReportData';
import { getKnownTracker } from '../../../lib/getKnownTracker';
import { InteractiveRequestIdBaseProps } from '../InteractiveRequestId';
import { GetsDbEventsByIndex } from '../../../State/EvidenceDB/DbEventCache';
import { HasCallStackResolution } from '../../../lib/Causation/CallStackResolution';
import { LicenseRequiredView } from '../LicenseRequiredView';
import { Literal, SearchDetailsRenderOnFirstExpand } from '../../../Styles/ReportStyles';
import { HasLicensePermissions } from '../../../State/EvidenceDB/LicensePermissions';

export const DomainWithKnownTrackerInParensView = ({domain}: {domain: string}) => {
	const knownTracker = getKnownTracker(domain);
	const knownTrackerName = knownTracker?.trackerKnownAs ?? knownTracker?.organizationKnownAs ??
		knownTracker?.disconnectMeName ?? knownTracker?.ghosteryOrganizationName;
	return (<>
		<Literal>{domain}</Literal>{ knownTrackerName == null ? null :
			(<>&nbsp;({knownTrackerName})</>)
		}</>
	)
}

export const IpReportView = observer( ({
	searchResultsObserver,
	causationGraphObserver,
	getsDbEventsByIndex,
	ipAddress,
	...rest
}: {
	searchResultsObserver: SearchResultsObserver;
	getsDbEventsByIndex: GetsDbEventsByIndex;
	causationGraphObserver?: CausationGraphObserver;
	ipAddress?: string;
} & InteractiveRequestIdBaseProps & HasCallStackResolution & HasLicensePermissions) => {
	const {licensePermissions} = rest;
	const {summary, byDestination} = new IpReportData(searchResultsObserver, ipAddress);
	const filteredSummary = summary.filter( s => licensePermissions.mayGenerateIpReportsAt(s) )
	if (filteredSummary.length === 0 && summary.length > 0) {
		return (<LicenseRequiredView />)
	}
	return (<div className={rcss.report}>
		<h1>IP Addresses Detected</h1>
		{filteredSummary.map(({pageRegisteredDomain, directions}) => (
			<section className={rcss.reportSection} key={`summary:${pageRegisteredDomain}`}>
				<h2>Summary for Website(s) Under <Literal>{pageRegisteredDomain}</Literal></h2>
				{ directions.map( ({direction, destinationDomains }) => (
					<div className={rcss.indented} key={direction}>
						<h3>{direction}</h3>
						<ol>{destinationDomains.map( (domain) => 
							<li key={domain}><DomainWithKnownTrackerInParensView {...{domain}} /></li>
						)}</ol>
					</div>
				))
				}
			</section>))
		}
		{byDestination.map(({ destinationDomain, pages }) => (
			<section className={rcss.reportSection} key={destinationDomain}>
				<h2><DomainWithKnownTrackerInParensView domain={destinationDomain} /></h2>
				<div className={rcss.indented}>{pages.map(({ pageUrl, directions }) => (
					<div className={rcss.indented} key={pageUrl} >
						<h3>
							<Literal>{pageUrl}</Literal>
						</h3>{
							directions.map(({ direction, encodings }) => (
							<div className={rcss.indented} key={`${direction}}`}>
								<h4>
									<Literal>{direction}</Literal>
								</h4>
								{encodings.map(({ encodingString, eventsAndResults, inferredTypeNames }, _index) => (
									<div className={rcss.indented} key={encodingString}>
										<InferredTypeNamesBlockView {...{inferredTypeNames}} />
										<SearchDetailsRenderOnFirstExpand key={encodingString} deferredRenderOnExpand={() => (<>{eventsAndResults.map((result) => (
												<SearchResultDetailsView
													key={`${result.eventIndex}\0${result.searchResult.encodedSearchTerm}\0${result.searchResult.position}\0${enhancedContentSourceLocation(result.searchResult)}`}
													{...{result, causationGraphObserver, getsDbEventsByIndex, ...rest}} />
											))
										}</>)}>
											<summary>
												<span className={rcss.searchResultEncodingFoundSpan}>{encodingString}</span>&#32;found at…
											</summary>
										</SearchDetailsRenderOnFirstExpand>
									</div>
								))}
							</div>
						))}
				</div>
				))}
			</div>
			</section>
		))}</div>);
	});
